import React, { useState, useEffect, useRef } from 'react';

export default function FaceImageCropModal({
  isOpen,
  ring,
  onApply,
  onClose
}) {
  const [tempPanX, setTempPanX] = useState(0);
  const [tempPanY, setTempPanY] = useState(0);
  const [tempZoom, setTempZoom] = useState(1);
  const [tempFrameW, setTempFrameW] = useState(120);
  const [tempFrameH, setTempFrameH] = useState(120);
  const [cropShape, setCropShape] = useState('circle');
  const [tempInvert, setTempInvert] = useState(false);
  const [tempGrayscale, setTempGrayscale] = useState(false);
  const [tempMonochrome, setTempMonochrome] = useState(false);
  const [tempContrast, setTempContrast] = useState(400);
  const [tempThreshold, setTempThreshold] = useState(50);
  const [tempFlipH, setTempFlipH] = useState(false);
  const [tempFlipV, setTempFlipV] = useState(false);
  const [lockAspect, setLockAspect] = useState(false);
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });

  const isPanningRef = useRef(false);
  const panStartRef = useRef({ startX: 0, startY: 0, initialPanX: 0, initialPanY: 0 });
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef(null);

  useEffect(() => {
    if (ring && isOpen) {
      setTempPanX(ring.imgPanX || 0);
      setTempPanY(ring.imgPanY || 0);
      setTempZoom(ring.imgZoom || 1);
      setTempFrameW(ring.width || 120);
      setTempFrameH(ring.height || 120);
      setCropShape(ring.cropShape || 'circle');
      setTempInvert(ring.invertColors || false);
      setTempGrayscale(ring.isGrayscale || ring.imageGrayscale || ring.grayscale === true || false);
      setTempMonochrome(ring.monochrome || false);
      setTempContrast(ring.monochromeContrast !== undefined ? ring.monochromeContrast : (ring.contrast !== undefined ? ring.contrast : 400));
      setTempThreshold(ring.monochromeThreshold !== undefined ? ring.monochromeThreshold : 50);
      setTempFlipH(ring.flipH || false);
      setTempFlipV(ring.flipV || false);
      setLockAspect(ring.constrainSize !== false);

      if (ring.imageUrl) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          setImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
        };
        img.src = ring.imageUrl;
      }
    }
  }, [ring, isOpen]);

  if (!isOpen || !ring) return null;

  const workspaceW = 460;
  const workspaceH = 300;

  // Compute visual scale of the frame to fit inside the workspace
  const scaleFit = Math.min((workspaceW - 60) / Math.max(1, tempFrameW), (workspaceH - 60) / Math.max(1, tempFrameH), 1.8);
  const cropW = Math.max(40, Math.round(tempFrameW * scaleFit));
  const cropH = Math.max(40, Math.round(tempFrameH * scaleFit));
  const cropLeft = (workspaceW - cropW) / 2;
  const cropTop = (workspaceH - cropH) / 2;
  const cropCenterX = workspaceW / 2;
  const cropCenterY = workspaceH / 2;

  // Base image display sizing inside aperture
  const naturalAspect = imgNaturalSize.w && imgNaturalSize.h ? imgNaturalSize.w / imgNaturalSize.h : (cropW / cropH);
  let baseW = cropW;
  let baseH = cropH;
  if (naturalAspect > cropW / cropH) {
    baseW = cropH * naturalAspect;
    baseH = cropH;
  } else {
    baseW = cropW;
    baseH = cropW / naturalAspect;
  }

  // Pointer drag to pan image
  const handleWorkspacePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    isPanningRef.current = true;
    panStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialPanX: tempPanX,
      initialPanY: tempPanY
    };

    const handlePointerMove = (moveEvt) => {
      if (!isPanningRef.current) return;
      const dx = moveEvt.clientX - panStartRef.current.startX;
      const dy = moveEvt.clientY - panStartRef.current.startY;
      const deltaPctX = (dx / cropW) * 100;
      const deltaPctY = (dy / cropH) * 100;
      setTempPanX(Math.round((panStartRef.current.initialPanX + deltaPctX) * 10) / 10);
      setTempPanY(Math.round((panStartRef.current.initialPanY + deltaPctY) * 10) / 10);
    };

    const handlePointerUp = () => {
      isPanningRef.current = false;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Mouse wheel to zoom
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY < 0 ? 0.05 : -0.05;
    setTempZoom(z => Math.max(0.2, Math.min(5, Math.round((z + delta) * 100) / 100)));
  };

  // Crop aperture resize handles
  const handleCropResizePointerDown = (handle, e) => {
    e.stopPropagation();
    e.preventDefault();
    isResizingRef.current = true;
    resizeStartRef.current = {
      handle,
      startX: e.clientX,
      startY: e.clientY,
      startW: tempFrameW,
      startH: tempFrameH
    };

    const handlePointerMove = (moveEvt) => {
      if (!isResizingRef.current || !resizeStartRef.current) return;
      const { handle: h, startX, startY, startW, startH } = resizeStartRef.current;
      const dx = (moveEvt.clientX - startX) / scaleFit;
      const dy = (moveEvt.clientY - startY) / scaleFit;

      let newW = startW;
      let newH = startH;

      if (h.includes('e')) newW = Math.max(30, Math.min(200, Math.round(startW + dx * 2)));
      if (h.includes('w')) newW = Math.max(30, Math.min(200, Math.round(startW - dx * 2)));
      if (h.includes('s')) newH = Math.max(30, Math.min(200, Math.round(startH + dy * 2)));
      if (h.includes('n')) newH = Math.max(30, Math.min(200, Math.round(startH - dy * 2)));

      if (lockAspect) {
        const aspect = startW / startH;
        if (h === 'e' || h === 'w') newH = Math.round(newW / aspect);
        else if (h === 'n' || h === 's') newW = Math.round(newH * aspect);
        else {
          const delta = Math.abs(newW - startW) > Math.abs(newH - startH) ? (newW - startW) : (newH - startH);
          newW = Math.max(30, Math.min(200, Math.round(startW + delta)));
          newH = Math.max(30, Math.min(200, Math.round(startH + delta)));
        }
      }

      setTempFrameW(newW);
      setTempFrameH(newH);
    };

    const handlePointerUp = () => {
      isResizingRef.current = false;
      resizeStartRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Fit to Image: scales the aperture or zoom so the photo fits nicely
  const handleFitCropToImage = () => {
    if (imgNaturalSize.w && imgNaturalSize.h) {
      const natAspect = imgNaturalSize.w / imgNaturalSize.h;
      let newW = tempFrameW;
      let newH = Math.round(newW / natAspect);
      if (newH > 200) {
        newH = 200;
        newW = Math.round(newH * natAspect);
      }
      setTempFrameW(newW);
      setTempFrameH(newH);
    }
    setTempZoom(1);
    setTempPanX(0);
    setTempPanY(0);
  };

  // Presets
  const applyPresetRatio = (wRatio, hRatio) => {
    const targetAspect = wRatio / hRatio;
    let newW = tempFrameW;
    let newH = Math.round(newW / targetAspect);
    if (newH > 200) {
      newH = 200;
      newW = Math.round(newH * targetAspect);
    }
    setTempFrameW(newW);
    setTempFrameH(newH);
  };

  const handleSwapOrientation = () => {
    const prevW = tempFrameW;
    const prevH = tempFrameH;
    setTempFrameW(prevH);
    setTempFrameH(prevW);
  };

  const handleApply = () => {
    onApply({
      imgPanX: tempPanX,
      imgPanY: tempPanY,
      imgZoom: tempZoom,
      width: tempFrameW,
      height: tempFrameH,
      maskRadius: Math.round(Math.min(tempFrameW, tempFrameH) / 2),
      cropShape: cropShape,
      cropApplied: true,
      imgAspect: naturalAspect,
      naturalWidth: imgNaturalSize.w,
      naturalHeight: imgNaturalSize.h,
      invertColors: tempInvert,
      isGrayscale: tempGrayscale,
      monochrome: tempMonochrome,
      monochromeContrast: tempContrast,
      monochromeThreshold: tempThreshold,
      flipH: tempFlipH,
      flipV: tempFlipV
    });
    onClose();
  };

  const handleReset = () => {
    setTempPanX(0);
    setTempPanY(0);
    setTempZoom(1);
    setTempInvert(false);
    setTempGrayscale(false);
    setTempMonochrome(false);
    setTempContrast(400);
    setTempThreshold(50);
    setTempFlipH(false);
    setTempFlipV(false);
  };

  return (
    <div
      data-testid="face-image-crop-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '560px',
          maxWidth: '95vw',
          background: 'var(--card-bg, #1b1b1d)',
          borderRadius: '10px',
          border: '1px solid var(--border-color, #3c3c42)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-color, #2d2d30)', padding: '12px 18px', background: 'var(--panel-bg, #18181a)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main, #dfdfe3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Pan & Zoom Image Crop
            </span>
            <span style={{ fontSize: '10px', color: '#8e8e93', backgroundColor: '#2b2b2f', padding: '2px 6px', borderRadius: '4px' }}>
              Frame: {Math.round(tempFrameW)} × {Math.round(tempFrameH)} px
            </span>
            <button
              type="button"
              data-testid="header-fit-crop-to-image-btn"
              onClick={handleFitCropToImage}
              style={{
                backgroundColor: '#1e293b',
                color: '#38bdf8',
                border: '1px solid #0284c7',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
              title="Fit crop frame to match photo's natural proportions"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
              <span>Fit to Image</span>
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ backgroundColor: 'transparent', border: 'none', color: '#8e8e93', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title="Close Modal"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Direct Manipulation Viewport */}
        <div style={{
          padding: '14px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#111113',
          borderBottom: '1px solid #2d2d30',
          userSelect: 'none'
        }}>
          {/* Pan/Zoom Workspace where full image is visible */}
          <div
            onPointerDown={handleWorkspacePointerDown}
            onWheel={handleWheel}
            style={{
              width: `${workspaceW}px`,
              height: `${workspaceH}px`,
              borderRadius: '8px',
              position: 'relative',
              overflow: 'hidden',
              cursor: isPanningRef.current ? 'grabbing' : 'grab',
              backgroundColor: '#0a0a0c',
              backgroundImage: 'radial-gradient(#26262b 1px, transparent 1px)',
              backgroundSize: '16px 16px',
              touchAction: 'none'
            }}
            title="Click and drag to pan image • Scroll wheel to zoom"
          >
            {ring.imageUrl ? (() => {
              const cropFilters = [];
              if (tempGrayscale || tempMonochrome) {
                cropFilters.push('grayscale(100%)');
              }
              if (tempMonochrome) {
                cropFilters.push(`contrast(${tempContrast}%)`);
                if (tempThreshold !== 50) {
                  const brightnessVal = Math.round(100 + (50 - tempThreshold) * 1.5);
                  cropFilters.push(`brightness(${brightnessVal}%)`);
                }
              }
              if (tempInvert) {
                cropFilters.push('invert(100%)');
              }
              return (
                <img
                  src={ring.imageUrl}
                  crossOrigin="anonymous"
                  alt="Subject Preview"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: `${cropCenterX + (tempPanX / 100) * cropW}px`,
                    top: `${cropCenterY + (tempPanY / 100) * cropH}px`,
                    width: `${baseW}px`,
                    height: `${baseH}px`,
                    transform: `translate(-50%, -50%) scale(${tempZoom}) ${tempFlipH ? 'scaleX(-1)' : ''} ${tempFlipV ? 'scaleY(-1)' : ''}`.trim(),
                    transformOrigin: 'center center',
                    userSelect: 'none',
                    pointerEvents: 'none',
                    filter: cropFilters.length > 0 ? cropFilters.join(' ') : undefined
                  }}
                />
              );
            })() : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#71717a', fontSize: '11px' }}>
                No Image Loaded
              </div>
            )}

            {/* Crop Aperture Frame: Inside is clear, outside is dimmed via box-shadow */}
            <div
              data-testid="crop-aperture-frame"
              style={{
                position: 'absolute',
                left: `${cropLeft}px`,
                top: `${cropTop}px`,
                width: `${cropW}px`,
                height: `${cropH}px`,
                borderRadius: cropShape === 'circle' ? '50%' : '6px',
                border: '2px solid #1677ff',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65)',
                pointerEvents: 'none',
                zIndex: 10
              }}
            >
              {/* Rule of Thirds Guides */}
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.35, borderRadius: cropShape === 'circle' ? '50%' : '6px', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '33.33%', left: 0, right: 0, height: '1px', borderTop: '1px dashed #ffffff' }} />
                <div style={{ position: 'absolute', top: '66.66%', left: 0, right: 0, height: '1px', borderTop: '1px dashed #ffffff' }} />
                <div style={{ position: 'absolute', left: '33.33%', top: 0, bottom: 0, width: '1px', borderLeft: '1px dashed #ffffff' }} />
                <div style={{ position: 'absolute', left: '66.66%', top: 0, bottom: 0, width: '1px', borderLeft: '1px dashed #ffffff' }} />
              </div>

              {/* Center Target Crosshair */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '12px',
                height: '12px',
                pointerEvents: 'none',
                opacity: 0.45
              }}>
                <div style={{ position: 'absolute', top: '5px', left: 0, right: 0, height: '2px', backgroundColor: '#ffffff' }} />
                <div style={{ position: 'absolute', left: '5px', top: 0, bottom: 0, width: '2px', backgroundColor: '#ffffff' }} />
              </div>

              {/* Resize Handles on the Crop Aperture */}
              <div
                onPointerDown={(e) => handleCropResizePointerDown('nw', e)}
                title="Drag to resize crop"
                data-testid="crop-handle-nw"
                style={{
                  position: 'absolute',
                  top: -5,
                  left: -5,
                  width: 10,
                  height: 10,
                  backgroundColor: '#ffffff',
                  border: '2px solid #1677ff',
                  borderRadius: '2px',
                  cursor: 'nwse-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('ne', e)}
                title="Drag to resize crop"
                data-testid="crop-handle-ne"
                style={{
                  position: 'absolute',
                  top: -5,
                  right: -5,
                  width: 10,
                  height: 10,
                  backgroundColor: '#ffffff',
                  border: '2px solid #1677ff',
                  borderRadius: '2px',
                  cursor: 'nesw-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('sw', e)}
                title="Drag to resize crop"
                data-testid="crop-handle-sw"
                style={{
                  position: 'absolute',
                  bottom: -5,
                  left: -5,
                  width: 10,
                  height: 10,
                  backgroundColor: '#ffffff',
                  border: '2px solid #1677ff',
                  borderRadius: '2px',
                  cursor: 'nesw-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('se', e)}
                title="Drag to resize crop"
                data-testid="crop-handle-se"
                style={{
                  position: 'absolute',
                  bottom: -5,
                  right: -5,
                  width: 10,
                  height: 10,
                  backgroundColor: '#ffffff',
                  border: '2px solid #1677ff',
                  borderRadius: '2px',
                  cursor: 'nwse-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('n', e)}
                title="Drag to adjust crop height"
                data-testid="crop-handle-n"
                style={{
                  position: 'absolute',
                  top: -4,
                  left: 'calc(50% - 14px)',
                  width: 28,
                  height: 8,
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #1677ff',
                  borderRadius: '4px',
                  cursor: 'ns-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('s', e)}
                title="Drag to adjust crop height"
                data-testid="crop-handle-s"
                style={{
                  position: 'absolute',
                  bottom: -4,
                  left: 'calc(50% - 14px)',
                  width: 28,
                  height: 8,
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #1677ff',
                  borderRadius: '4px',
                  cursor: 'ns-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('w', e)}
                title="Drag to adjust crop width"
                data-testid="crop-handle-w"
                style={{
                  position: 'absolute',
                  left: -4,
                  top: 'calc(50% - 14px)',
                  width: 8,
                  height: 28,
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #1677ff',
                  borderRadius: '4px',
                  cursor: 'ew-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
              <div
                onPointerDown={(e) => handleCropResizePointerDown('e', e)}
                title="Drag to adjust crop width"
                data-testid="crop-handle-e"
                style={{
                  position: 'absolute',
                  right: -4,
                  top: 'calc(50% - 14px)',
                  width: 8,
                  height: 28,
                  backgroundColor: '#ffffff',
                  border: '1.5px solid #1677ff',
                  borderRadius: '4px',
                  cursor: 'ew-resize',
                  pointerEvents: 'auto',
                  zIndex: 30
                }}
              />
            </div>
          </div>

          {/* Hint */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: `${workspaceW}px`, marginTop: '8px', color: '#8e8e93', fontSize: '10px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><polyline points="15 19 12 22 9 19" /><polyline points="19 9 22 12 19 15" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" />
                </svg>
                <span>Drag photo to pan</span>
              </span>
              <span>•</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                </svg>
                <span>Scroll wheel to zoom</span>
              </span>
              <span>•</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                </svg>
                <span>Drag handles to resize aperture</span>
              </span>
            </span>
            <span style={{ color: '#71717a' }}>
              Dimmed area shows uncropped photo
            </span>
          </div>
        </div>

        {/* Controls Bar */}
        <div style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '10px', backgroundColor: '#1b1b1d', maxHeight: '320px', overflowY: 'auto' }}>
          {/* Row 1: Dimensions & Presets */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: '8px', borderBottom: '1px solid #2d2d30' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#dfdfe3' }}>
                Crop Aperture & Aspect Ratio
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>W:</span>
                  <input
                    type="number"
                    data-testid="crop-width-input"
                    value={Math.round(tempFrameW)}
                    onChange={(e) => {
                      const val = Math.max(20, Math.min(200, parseInt(e.target.value) || 20));
                      setTempFrameW(val);
                      if (lockAspect) setTempFrameH(val);
                    }}
                    style={{
                      width: '46px',
                      backgroundColor: '#26262b',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: '#dfdfe3',
                      fontSize: '10px',
                      padding: '2px 4px',
                      textAlign: 'center'
                    }}
                    min="20"
                    max="200"
                  />
                  <span style={{ fontSize: '9px', color: '#71717a' }}>px</span>
                </div>
                <span style={{ color: '#71717a', fontSize: '10px' }}>×</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <span style={{ fontSize: '10px', color: '#8e8e93' }}>H:</span>
                  <input
                    type="number"
                    data-testid="crop-height-input"
                    value={Math.round(tempFrameH)}
                    onChange={(e) => {
                      const val = Math.max(20, Math.min(200, parseInt(e.target.value) || 20));
                      setTempFrameH(val);
                      if (lockAspect) setTempFrameW(val);
                    }}
                    style={{
                      width: '46px',
                      backgroundColor: '#26262b',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      color: '#dfdfe3',
                      fontSize: '10px',
                      padding: '2px 4px',
                      textAlign: 'center'
                    }}
                    min="20"
                    max="200"
                  />
                  <span style={{ fontSize: '9px', color: '#71717a' }}>px</span>
                </div>
                <button
                  type="button"
                  data-testid="crop-lock-ratio-btn"
                  onClick={() => setLockAspect(l => !l)}
                  style={{
                    backgroundColor: lockAspect ? '#1677ff' : '#26262b',
                    color: '#ffffff',
                    border: '1px solid #3c3c42',
                    borderRadius: '3px',
                    padding: '3px 6px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={lockAspect ? "Aspect ratio locked" : "Aspect ratio unlocked"}
                >
                  {lockAspect ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Presets buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                data-testid="preset-1-1"
                onClick={() => applyPresetRatio(1, 1)}
                style={{
                  backgroundColor: tempFrameW === tempFrameH ? '#1677ff' : '#26262b',
                  color: '#ffffff',
                  border: '1px solid #3c3c42',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                1:1 (Circle/Square)
              </button>
              <button
                type="button"
                data-testid="preset-4-3"
                onClick={() => applyPresetRatio(4, 3)}
                style={{
                  backgroundColor: Math.abs(tempFrameW / tempFrameH - 4 / 3) < 0.05 ? '#1677ff' : '#26262b',
                  color: '#ffffff',
                  border: '1px solid #3c3c42',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                4:3
              </button>
              <button
                type="button"
                data-testid="preset-16-9"
                onClick={() => applyPresetRatio(16, 9)}
                style={{
                  backgroundColor: Math.abs(tempFrameW / tempFrameH - 16 / 9) < 0.05 ? '#1677ff' : '#26262b',
                  color: '#ffffff',
                  border: '1px solid #3c3c42',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  fontSize: '10px',
                  cursor: 'pointer'
                }}
              >
                16:9
              </button>
              <button
                type="button"
                data-testid="swap-crop-orientation-btn"
                onClick={handleSwapOrientation}
                style={{
                  backgroundColor: '#26262b',
                  color: '#dfdfe3',
                  border: '1px solid #3c3c42',
                  borderRadius: '3px',
                  padding: '2px 6px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title="Swap orientation (Rotate frame 90°)"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                <span>Rotate</span>
              </button>
              <button
                type="button"
                data-testid="fit-crop-to-image-btn"
                onClick={handleFitCropToImage}
                style={{
                  backgroundColor: '#1e293b',
                  color: '#38bdf8',
                  border: '1px solid #0284c7',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginLeft: 'auto'
                }}
              >
                Fit Crop to Image
              </button>
            </div>

            {/* Row 1b: Crop Shape & Coin Layer Presets */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', paddingTop: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Shape:</span>
                <button
                  type="button"
                  data-testid="crop-shape-circle-btn"
                  onClick={() => setCropShape('circle')}
                  style={{
                    backgroundColor: cropShape === 'circle' ? '#1677ff' : '#26262b',
                    color: '#ffffff',
                    border: '1px solid #3c3c42',
                    borderRadius: '3px',
                    padding: '2px 8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <circle cx="12" cy="12" r="9" />
                  </svg>
                  <span>Circle</span>
                </button>
                <button
                  type="button"
                  data-testid="crop-shape-rect-btn"
                  onClick={() => setCropShape('rect')}
                  style={{
                    backgroundColor: cropShape === 'rect' ? '#1677ff' : '#26262b',
                    color: '#ffffff',
                    border: '1px solid #3c3c42',
                    borderRadius: '3px',
                    padding: '2px 8px',
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ⬜ Rectangle
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '10px', color: '#8e8e93' }}>Coin Size:</span>
                {[
                  { label: '200px', size: 200 },
                  { label: '160px', size: 160 },
                  { label: '130px', size: 130 },
                  { label: '100px', size: 100 }
                ].map(p => (
                  <button
                    key={p.size}
                    type="button"
                    onClick={() => {
                      setTempFrameW(p.size);
                      setTempFrameH(p.size);
                    }}
                    style={{
                      backgroundColor: tempFrameW === p.size && tempFrameH === p.size ? '#1677ff' : '#26262b',
                      color: tempFrameW === p.size && tempFrameH === p.size ? '#ffffff' : '#dfdfe3',
                      border: '1px solid #3c3c42',
                      borderRadius: '3px',
                      padding: '2px 6px',
                      fontSize: '9px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 2: Zoom & Pan Sliders */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {/* Zoom */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#8e8e93' }}>
                <span>Zoom Scale</span>
                <span style={{ color: '#38bdf8', fontFamily: 'monospace' }}>{tempZoom.toFixed(2)}x</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => setTempZoom(z => Math.max(0.2, Math.round((z - 0.1) * 10) / 10))}
                  style={{ backgroundColor: '#26262b', border: '1px solid #3c3c42', color: '#fff', borderRadius: '3px', width: '20px', height: '20px', cursor: 'pointer', fontSize: '11px' }}
                >
                  -
                </button>
                <input
                  type="range"
                  data-testid="crop-zoom-slider"
                  min="0.2"
                  max="5"
                  step="0.05"
                  value={tempZoom}
                  onChange={(e) => setTempZoom(parseFloat(e.target.value))}
                  style={{ flex: 1, accentColor: '#1677ff' }}
                />
                <button
                  type="button"
                  onClick={() => setTempZoom(z => Math.min(5, Math.round((z + 0.1) * 10) / 10))}
                  style={{ backgroundColor: '#26262b', border: '1px solid #3c3c42', color: '#fff', borderRadius: '3px', width: '20px', height: '20px', cursor: 'pointer', fontSize: '11px' }}
                >
                  +
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                {['1.0', '1.25', '1.5', '2.0'].map(zVal => (
                  <button
                    key={zVal}
                    type="button"
                    onClick={() => setTempZoom(parseFloat(zVal))}
                    style={{
                      flex: 1,
                      backgroundColor: Math.abs(tempZoom - parseFloat(zVal)) < 0.04 ? '#1677ff' : '#26262b',
                      color: Math.abs(tempZoom - parseFloat(zVal)) < 0.04 ? '#ffffff' : '#8e8e93',
                      border: '1px solid #3c3c42',
                      borderRadius: '2px',
                      padding: '1px 3px',
                      fontSize: '9px',
                      cursor: 'pointer'
                    }}
                  >
                    {zVal}x
                  </button>
                ))}
              </div>
            </div>

            {/* Tone, Invert & Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', justifyContent: 'space-between' }}>
              {/* Grayscale, Invert, Monochrome buttons */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {/* Grayscale Button */}
                <button
                  type="button"
                  data-testid="crop-grayscale-btn"
                  onClick={() => setTempGrayscale(g => !g)}
                  style={{
                    flex: 1,
                    backgroundColor: tempGrayscale ? 'rgba(22, 119, 255, 0.2)' : '#26262b',
                    color: tempGrayscale ? '#58a6ff' : '#dfdfe3',
                    border: `1px solid ${tempGrayscale ? '#1677ff' : '#3c3c42'}`,
                    borderRadius: '3px',
                    padding: '3px 2px',
                    fontSize: '9px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px'
                  }}
                  title="Convert to black & white grayscale"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="12" y1="3" x2="12" y2="21" />
                    </svg>
                    <span>Gray</span>
                  </span>
                  <span style={{ fontSize: '8px', opacity: 0.8 }}>({tempGrayscale ? 'ON' : 'OFF'})</span>
                </button>

                {/* Invert Colors (Negative) Button */}
                <button
                  type="button"
                  data-testid="crop-invert-btn"
                  onClick={() => setTempInvert(inv => !inv)}
                  style={{
                    flex: 1,
                    backgroundColor: tempInvert ? 'rgba(22, 119, 255, 0.2)' : '#26262b',
                    color: tempInvert ? '#58a6ff' : '#dfdfe3',
                    border: `1px solid ${tempInvert ? '#1677ff' : '#3c3c42'}`,
                    borderRadius: '3px',
                    padding: '3px 2px',
                    fontSize: '9px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px'
                  }}
                  title="Invert photo colors (Negative)"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 2a10 10 0 0 1 0 20z" fill="currentColor" />
                    </svg>
                    <span>Invert</span>
                  </span>
                  <span style={{ fontSize: '8px', opacity: 0.8 }}>({tempInvert ? 'ON' : 'OFF'})</span>
                </button>

                {/* Monochrome Button */}
                <button
                  type="button"
                  data-testid="crop-monochrome-btn"
                  onClick={() => {
                    setTempMonochrome(m => {
                      const next = !m;
                      if (next && !tempGrayscale) setTempGrayscale(true);
                      return next;
                    });
                  }}
                  style={{
                    flex: 1,
                    backgroundColor: tempMonochrome ? 'rgba(22, 119, 255, 0.2)' : '#26262b',
                    color: tempMonochrome ? '#58a6ff' : '#dfdfe3',
                    border: `1px solid ${tempMonochrome ? '#1677ff' : '#3c3c42'}`,
                    borderRadius: '3px',
                    padding: '3px 2px',
                    fontSize: '9px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px'
                  }}
                  title="Clip grayscale into high-contrast 2-tone monochrome"
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <polygon points="3,3 21,3 3,21" fill="currentColor" stroke="none" />
                    </svg>
                    <span>Mono</span>
                  </span>
                  <span style={{ fontSize: '8px', opacity: 0.8 }}>({tempMonochrome ? 'ON' : 'OFF'})</span>
                </button>
              </div>

              {/* Monochrome Sliders if enabled */}
              {tempMonochrome && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', padding: '4px 6px', backgroundColor: '#141416', borderRadius: '4px', border: '1px solid #2d2d30' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: '#8e8e93' }}>
                      <span>Contrast</span>
                      <span style={{ fontFamily: 'monospace', color: '#58a6ff' }}>{tempContrast}%</span>
                    </div>
                    <input
                      type="range"
                      data-testid="crop-monochrome-contrast-slider"
                      min="100"
                      max="1000"
                      step="10"
                      value={tempContrast}
                      onChange={(e) => setTempContrast(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#1677ff', height: '3px' }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '8px', color: '#8e8e93' }}>
                      <span>Threshold</span>
                      <span style={{ fontFamily: 'monospace', color: '#58a6ff' }}>{tempThreshold}%</span>
                    </div>
                    <input
                      type="range"
                      data-testid="crop-monochrome-threshold-slider"
                      min="5"
                      max="95"
                      step="1"
                      value={tempThreshold}
                      onChange={(e) => setTempThreshold(parseInt(e.target.value))}
                      style={{ width: '100%', accentColor: '#1677ff', height: '3px' }}
                    />
                  </div>
                </div>
              )}

              {/* Flips & Reset Row */}
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  type="button"
                  data-testid="crop-flip-h-btn"
                  onClick={() => setTempFlipH(f => !f)}
                  style={{
                    flex: 1,
                    backgroundColor: tempFlipH ? 'rgba(22, 119, 255, 0.2)' : '#26262b',
                    color: tempFlipH ? '#58a6ff' : '#dfdfe3',
                    border: `1px solid ${tempFlipH ? '#1677ff' : '#3c3c42'}`,
                    borderRadius: '3px',
                    padding: '3px 4px',
                    fontSize: '9px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  title="Flip horizontally"
                >
                  ↔️ Flip H
                </button>
                <button
                  type="button"
                  data-testid="crop-flip-v-btn"
                  onClick={() => setTempFlipV(f => !f)}
                  style={{
                    flex: 1,
                    backgroundColor: tempFlipV ? 'rgba(22, 119, 255, 0.2)' : '#26262b',
                    color: tempFlipV ? '#58a6ff' : '#dfdfe3',
                    border: `1px solid ${tempFlipV ? '#1677ff' : '#3c3c42'}`,
                    borderRadius: '3px',
                    padding: '3px 4px',
                    fontSize: '9px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  title="Flip vertically"
                >
                  ↕️ Flip V
                </button>
                <button
                  type="button"
                  onClick={() => { setTempPanX(0); setTempPanY(0); }}
                  style={{
                    flex: 1,
                    backgroundColor: '#26262b',
                    color: '#dfdfe3',
                    border: '1px solid #3c3c42',
                    borderRadius: '3px',
                    padding: '3px',
                    fontSize: '9px',
                    cursor: 'pointer'
                  }}
                >
                  Center
                </button>
                <button
                  type="button"
                  data-testid="crop-reset-btn"
                  onClick={handleReset}
                  style={{
                    flex: 1,
                    backgroundColor: '#26262b',
                    color: '#f87171',
                    border: '1px solid #7f1d1d',
                    borderRadius: '3px',
                    padding: '3px',
                    fontSize: '9px',
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Under-Zoom Warning Banner */}
          {tempZoom < 0.999 && (
            <div style={{
              padding: '6px 10px',
              backgroundColor: 'rgba(234, 179, 8, 0.12)',
              border: '1px solid rgba(234, 179, 8, 0.35)',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
              <span style={{ fontSize: '10px', color: '#facc15', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                Zoom is below 1.0x ({tempZoom.toFixed(2)}x). The image is smaller than the crop aperture — uncropped white corners or image edges will be visible on the coin.
              </span>
              <button
                type="button"
                onClick={() => setTempZoom(1)}
                style={{
                  backgroundColor: '#eab308',
                  color: '#000',
                  border: 'none',
                  borderRadius: '3px',
                  padding: '2px 8px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Fill Aperture (1.0x)
              </button>
            </div>
          )}

          {/* Row 3: Fine Pan Sliders */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                <span>Horizontal Pan (X)</span>
                <span style={{ fontFamily: 'monospace' }}>{Math.round(tempPanX)}%</span>
              </div>
              <input
                type="range"
                min="-100"
                max="100"
                value={tempPanX}
                onChange={(e) => setTempPanX(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#1677ff' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#8e8e93' }}>
                <span>Vertical Pan (Y)</span>
                <span style={{ fontFamily: 'monospace' }}>{Math.round(tempPanY)}%</span>
              </div>
              <input
                type="range"
                min="-100"
                max="100"
                value={tempPanY}
                onChange={(e) => setTempPanY(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#1677ff' }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--border-color, #2d2d30)', padding: '12px 18px', background: 'var(--panel-bg, #18181a)' }}>
          <span style={{ fontSize: '10px', color: 'var(--text-mute, #8e8e93)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            {tempZoom !== 1 || tempPanX !== 0 || tempPanY !== 0 ? (
              <>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#52c41a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>Crop active</span>
              </>
            ) : "Uncropped original"}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'var(--card-bg, #2b2b2f)',
                border: '1px solid var(--border-color, #3c3c42)',
                color: 'var(--text-main, #dfdfe3)',
                borderRadius: '4px',
                padding: '6px 14px',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="crop-apply-btn"
              onClick={handleApply}
              style={{
                background: 'var(--primary-gradient, var(--primary-color, #1677ff))',
                border: 'none',
                color: '#ffffff',
                borderRadius: '4px',
                padding: '6px 16px',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(22, 119, 255, 0.4)'
              }}
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
